import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { clientPortalAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Search, Clock, Shield, Download, Truck, ArrowRight, Bell, Bookmark, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stageLabels = {
  lead: 'Lead', qualified: 'Qualified', docs_pending: 'Docs Pending',
  submitted: 'Submitted to Lenders', approved: 'Approved', funded: 'Funded',
  closed_won: 'Closed - Won', closed_lost: 'Closed - Lost',
};

const stageColors = {
  lead: 'bg-zinc-200 text-zinc-700', qualified: 'bg-blue-100 text-blue-700',
  docs_pending: 'bg-amber-100 text-amber-700', submitted: 'bg-purple-100 text-purple-700',
  approved: 'bg-green-100 text-green-700', funded: 'bg-emerald-100 text-emerald-700',
  closed_won: 'bg-green-200 text-green-800', closed_lost: 'bg-red-100 text-red-700',
};

const stageOrder = ['lead', 'qualified', 'docs_pending', 'submitted', 'approved', 'funded', 'closed_won'];

export default function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [docs, setDocs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveSearchOpen, setSaveSearchOpen] = useState(false);
  const [searchName, setSearchName] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [d, doc, notif, ss] = await Promise.all([
          clientPortalAPI.deals(),
          clientPortalAPI.documents(),
          clientPortalAPI.notifications(),
          clientPortalAPI.savedSearches(),
        ]);
        setDeals(d.data || []);
        setDocs(doc.data || []);
        setNotifications(notif.data || []);
        setSavedSearches(ss.data || []);
      } catch (e) { console.error('Client portal load error:', e); }
      setLoading(false);
    };
    load();
  }, []);

  const saveSearch = async () => {
    if (!searchName.trim()) return;
    try {
      const r = await clientPortalAPI.saveSearch({ name: searchName, filters: {} });
      setSavedSearches(prev => [r.data, ...prev]);
      setSaveSearchOpen(false);
      setSearchName('');
      toast.success('Search saved');
    } catch { toast.error('Failed to save'); }
  };

  const deleteSavedSearch = async (id) => {
    try {
      await clientPortalAPI.deleteSavedSearch(id);
      setSavedSearches(prev => prev.filter(s => s.id !== id));
      toast.success('Search removed');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="client-dashboard">
      <h1 className="text-3xl font-bold uppercase text-[#0A0A0A] mb-2">Welcome, {user?.name}</h1>
      <p className="text-zinc-500 mb-8">Manage your account, view deals, and track applications.</p>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <Link to="/inventory">
          <Card className="border shadow-sm hover:shadow-md transition-all hover:border-[#E31937]/30 cursor-pointer">
            <CardContent className="p-5 flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E31937]/10 rounded-sm flex items-center justify-center flex-shrink-0"><Truck className="w-5 h-5 text-[#E31937]" /></div>
              <div><h3 className="font-semibold text-sm">Browse Inventory</h3><p className="text-zinc-500 text-xs">View trucks & trailers</p></div>
              <ArrowRight className="w-4 h-4 text-zinc-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/financing">
          <Card className="border shadow-sm hover:shadow-md transition-all hover:border-blue-500/30 cursor-pointer">
            <CardContent className="p-5 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5 text-blue-500" /></div>
              <div><h3 className="font-semibold text-sm">Apply for Financing</h3><p className="text-zinc-500 text-xs">Get pre-approved</p></div>
              <ArrowRight className="w-4 h-4 text-zinc-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>
        <Card className="border shadow-sm hover:shadow-md transition-all hover:border-green-500/30 cursor-pointer" onClick={() => setSaveSearchOpen(true)}>
          <CardContent className="p-5 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/10 rounded-sm flex items-center justify-center flex-shrink-0"><Bookmark className="w-5 h-5 text-green-500" /></div>
            <div><h3 className="font-semibold text-sm">Save a Search</h3><p className="text-zinc-500 text-xs">Get notified on matches</p></div>
            <ArrowRight className="w-4 h-4 text-zinc-400 ml-auto" />
          </CardContent>
        </Card>
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="mb-10">
          <h2 className="text-xl font-semibold uppercase text-[#0A0A0A] mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#E31937]" /> Notifications
          </h2>
          <div className="space-y-2">
            {notifications.slice(0, 5).map(n => (
              <Card key={n.id} className="border shadow-sm">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${n.status === 'sent' ? 'bg-green-500' : 'bg-amber-500'}`} />
                  <div className="flex-1">
                    <p className="text-sm text-gray-700">{n.message}</p>
                    <p className="text-xs text-gray-400">{new Date(n.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">{n.type?.replace('_', ' ')}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Active Deals with Timeline */}
      <div className="mb-10">
        <h2 className="text-xl font-semibold uppercase text-[#0A0A0A] mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#E31937]" /> My Deals
        </h2>
        {loading ? (
          <div className="space-y-3">{[1,2].map(i => <div key={i} className="animate-pulse h-20 bg-zinc-100 rounded" />)}</div>
        ) : deals.length === 0 ? (
          <Card className="border border-dashed"><CardContent className="p-8 text-center text-zinc-400"><p>No active deals. Your deals will appear here when created by your sales rep.</p></CardContent></Card>
        ) : (
          <div className="space-y-4">
            {deals.map(deal => {
              const currentIdx = stageOrder.indexOf(deal.stage);
              return (
                <Card key={deal.id} className="border shadow-sm overflow-hidden" data-testid={`deal-${deal.id}`}>
                  <CardContent className="p-0">
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {deal.unit && <img src={deal.unit.images?.[0] || ''} alt="" className="w-16 h-12 object-cover rounded" />}
                        <div>
                          <p className="font-medium text-sm">{deal.unit?.title || 'Deal'}</p>
                          {deal.amount && <p className="text-[#E31937] font-bold text-sm">${deal.amount.toLocaleString()}</p>}
                          <p className="text-zinc-500 text-xs">{deal.notes || 'No notes'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={stageColors[deal.stage] || 'bg-zinc-100'}>{stageLabels[deal.stage] || deal.stage}</Badge>
                        <p className="text-xs text-zinc-400 mt-1">{new Date(deal.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    {/* Progress timeline */}
                    <div className="border-t bg-gray-50 px-4 py-3">
                      <div className="flex items-center gap-1">
                        {stageOrder.slice(0, -1).map((s, i) => {
                          const done = i <= currentIdx;
                          const active = i === currentIdx;
                          return (
                            <div key={s} className="flex items-center flex-1">
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-[#E31937]' : 'bg-gray-200'} ${active ? 'ring-2 ring-[#E31937]/30' : ''}`}>
                                {done && <CheckCircle className="w-3 h-3 text-white" />}
                              </div>
                              {i < stageOrder.length - 2 && <div className={`h-0.5 flex-1 ${i < currentIdx ? 'bg-[#E31937]' : 'bg-gray-200'}`} />}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-[9px] text-gray-400">Lead</span>
                        <span className="text-[9px] text-gray-400">Funded</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Saved Searches */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold uppercase text-[#0A0A0A] flex items-center gap-2">
            <Search className="w-5 h-5 text-[#E31937]" /> Saved Searches
          </h2>
          <Button size="sm" variant="outline" onClick={() => setSaveSearchOpen(true)} data-testid="save-search-btn">
            <Bookmark className="w-3 h-3 mr-1" /> Save Search
          </Button>
        </div>
        {savedSearches.length === 0 ? (
          <Card className="border border-dashed"><CardContent className="p-6 text-center text-zinc-400 text-sm"><p>No saved searches. Save your inventory search criteria to get notified when matching units arrive.</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {savedSearches.map(s => (
              <Card key={s.id} className="border shadow-sm">
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bookmark className="w-4 h-4 text-[#E31937]" />
                    <div>
                      <p className="text-sm font-medium">{s.name}</p>
                      <p className="text-xs text-zinc-400">{new Date(s.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate('/inventory')}>View</Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-red-400 border-red-200 hover:bg-red-50" onClick={() => deleteSavedSearch(s.id)} data-testid={`delete-search-${s.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Documents */}
      <div>
        <h2 className="text-xl font-semibold uppercase text-[#0A0A0A] mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#E31937]" /> My Documents
        </h2>
        {loading ? (
          <div className="space-y-3">{[1,2].map(i => <div key={i} className="animate-pulse h-16 bg-zinc-100 rounded" />)}</div>
        ) : docs.length === 0 ? (
          <Card className="border border-dashed"><CardContent className="p-8 text-center text-zinc-400"><p>No documents yet. Quotes, invoices, and signed documents will appear here.</p></CardContent></Card>
        ) : (
          <div className="space-y-2">
            {docs.map(doc => (
              <Card key={doc.id} className="border shadow-sm">
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-zinc-400" />
                    <div>
                      <p className="font-medium text-sm capitalize">{doc.type?.replace('_', ' ')}</p>
                      <p className="text-xs text-zinc-500">{doc.filename} - {new Date(doc.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.total && <span className="text-sm font-bold text-[#E31937]">${doc.total.toLocaleString()}</span>}
                    <Button variant="outline" size="sm" asChild>
                      <a href={`${API_URL}${doc.url}`} target="_blank" rel="noopener noreferrer" data-testid={`download-doc-${doc.id}`}>
                        <Download className="w-3 h-3 mr-1" /> View
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Save Search Dialog */}
      <Dialog open={saveSearchOpen} onOpenChange={setSaveSearchOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader><DialogTitle>Save Search</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500">Search Name</label>
              <Input value={searchName} onChange={e => setSearchName(e.target.value)} placeholder="e.g. Volvo Trucks Under $150K" className="mt-1" data-testid="search-name-input" />
            </div>
            <p className="text-xs text-gray-400">You'll be notified when new inventory matches your criteria.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setSaveSearchOpen(false)}>Cancel</Button>
              <Button className="bg-[#E31937] text-white" onClick={saveSearch} disabled={!searchName.trim()} data-testid="confirm-save-search">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
